import React, { useEffect, useCallback, useRef } from 'react';
import { useDiary, BASE_PX_PER_MINUTE } from './DiaryContext';
import { useDrag } from './DragContext';

/**
 * DragOverlay - Invisible component that manages global pointer listeners
 * during both move and resize operations.
 * Visual indicators are rendered by StaffColumn.
 */
export const SHELF_STAFF_ID = '__shelf__';
const SHELF_DROP_ZONE_PX = 80;

function DragOverlay({ shelfRef }) {
  const { dragState, liveRef, columnsRef, updateDrag, updateResize, endDrag } = useDrag();
  const { state, dispatch, onEventDrop, onEventResize } = useDiary();
  const { viewStartMinutes, zoomLevel, slotDurationMinutes, parkedEvents } = state;
  const rafRef = useRef(null);

  // Refs for latest values so stable callbacks always read current state
  const stateRef = useRef({ viewStartMinutes, zoomLevel, slotDurationMinutes, selectedDate: state.selectedDate, parkedEvents });
  stateRef.current = { viewStartMinutes, zoomLevel, slotDurationMinutes, selectedDate: state.selectedDate, parkedEvents };

  const callbacksRef = useRef({ onEventDrop, onEventResize, dispatch });
  callbacksRef.current = { onEventDrop, onEventResize, dispatch };

  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;

  const resolveColumn = useCallback((clientX, clientY) => {
    // Check shelf drop zone (top 80px of viewport area)
    // Only activate shelf when NOT dragging FROM the shelf (allow dragging out)
    const isDraggingFromShelf = liveRef.current?.originStaffId === SHELF_STAFF_ID;
    if (!isDraggingFromShelf && shelfRef?.current && parkedEvents.length < 3) {
      const shelfRect = shelfRef.current.getBoundingClientRect();
      // Use the full shelf zone height, or 80px minimum for the drop target
      const zoneBottom = shelfRect.top + Math.max(shelfRect.height, SHELF_DROP_ZONE_PX);
      if (clientY >= shelfRect.top && clientY <= zoneBottom) {
        return { staffId: SHELF_STAFF_ID, rect: shelfRect };
      }
    }
    const cols = columnsRef.current;
    for (const staffId of Object.keys(cols)) {
      const el = cols[staffId];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return { staffId, rect };
      }
    }
    return null;
  }, [columnsRef, shelfRef, parkedEvents.length]);

  const snap = useCallback((minutes, slotDur) => {
    return Math.floor(minutes / slotDur) * slotDur;
  }, []);

  // ── Pointer move (handles both move and resize) ──
  const handleMove = useCallback((e) => {
    if (!liveRef.current) return;
    e.preventDefault();

    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!liveRef.current) return;
      const { viewStartMinutes: vsm, zoomLevel: zl, slotDurationMinutes: sdm } = stateRef.current;
      const mode = liveRef.current.mode;

      if (mode === 'move') {
        const col = resolveColumn(cx, cy);
        let staffId = liveRef.current.originStaffId;
        let minutes = liveRef.current.event.startMinutes;

        if (col) {
          staffId = col.staffId;
          if (staffId !== SHELF_STAFF_ID) {
            const offsetY = liveRef.current.offsetY || 0;
            const yInCol = cy - col.rect.top - offsetY;
            const rawMinutes = vsm + yInCol / (BASE_PX_PER_MINUTE * zl);
            minutes = snap(Math.max(vsm, rawMinutes), sdm);
          }
        }

        updateDrag(cx, cy, staffId, minutes);
      } else if (mode === 'resize') {
        const event = liveRef.current.event;
        const edge = liveRef.current.edge || 'bottom';
        const col = columnsRef.current[event.staffId];
        if (!col) return;
        const rect = col.getBoundingClientRect();
        const yInCol = cy - rect.top;
        const rawMinutes = vsm + yInCol / (BASE_PX_PER_MINUTE * zl);
        const snapped = snap(rawMinutes, sdm);

        if (edge === 'top') {
          const maxStart = event.endMinutes - sdm;
          updateResize(Math.min(maxStart, Math.max(vsm, snapped)));
        } else {
          const minEnd = event.startMinutes + sdm;
          updateResize(Math.max(minEnd, snapped));
        }
      }
    });
  }, [liveRef, columnsRef, resolveColumn, snap, updateDrag, updateResize]);

  // ── Pointer up (handles both move and resize) ──
  const handleUp = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (!liveRef.current) return;

    const mode = liveRef.current.mode;
    const event = liveRef.current.event;
    const { viewStartMinutes: vsm, zoomLevel: zl, slotDurationMinutes: sdm } = stateRef.current;

    if (mode === 'move') {
      const originStaffId = liveRef.current.originStaffId;
      const cx = liveRef.current.pointerX;
      const cy = liveRef.current.pointerY;
      const col = resolveColumn(cx, cy);

      let targetStaffId = originStaffId;
      let targetMinutes = event.startMinutes;

      if (col) {
        targetStaffId = col.staffId;
        if (targetStaffId !== SHELF_STAFF_ID) {
          const offsetY = liveRef.current.offsetY || 0;
          const yInCol = cy - col.rect.top - offsetY;
          const rawMinutes = vsm + yInCol / (BASE_PX_PER_MINUTE * zl);
          targetMinutes = snap(Math.max(vsm, rawMinutes), sdm);
        }
      }

      endDrag();

      // Handle shelf drop
      if (targetStaffId === SHELF_STAFF_ID) {
        callbacksRef.current.dispatch({ type: 'PARK_EVENT', payload: { event, originStaffId, originDate: stateRef.current.selectedDate } });
        return;
      }

      // Handle unpark (dragging from shelf to a column)
      if (originStaffId === SHELF_STAFF_ID && targetStaffId !== SHELF_STAFF_ID) {
        // Grab chain events from parked item before unparking removes it
        const parked = stateRef.current.parkedEvents?.find(p => p.event.id === event.id);
        const chainEvents = parked?.chainEvents || null;
        callbacksRef.current.dispatch({ type: 'UNPARK_EVENT', payload: { eventId: event.id, restore: false } });
        if (callbacksRef.current.onEventDrop) {
          const eventWithChain = chainEvents ? { ...event, _chainEvents: chainEvents } : event;
          callbacksRef.current.onEventDrop(eventWithChain, originStaffId, targetStaffId, targetMinutes);
        }
        return;
      }

      const moved = targetStaffId !== originStaffId || targetMinutes !== event.startMinutes;
      if (moved && callbacksRef.current.onEventDrop) {
        callbacksRef.current.onEventDrop(event, originStaffId, targetStaffId, targetMinutes);
      }
    } else if (mode === 'resize') {
      const newStart = dragStateRef.current?.newStartMinutes ?? event.startMinutes;
      const newEnd = dragStateRef.current?.newEndMinutes ?? event.endMinutes;
      endDrag();

      const changed = newStart !== event.startMinutes || newEnd !== event.endMinutes;
      if (changed && callbacksRef.current.onEventResize) {
        callbacksRef.current.onEventResize(event, newStart, newEnd);
      }
    } else {
      endDrag();
    }
  }, [liveRef, resolveColumn, snap, endDrag]);

  // Register/unregister listeners when drag starts/stops
  useEffect(() => {
    if (!dragState) return;

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);

    document.body.style.userSelect = 'none';
    if (dragState.mode === 'resize') {
      document.body.style.cursor = 'ns-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [!!dragState]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default React.memo(DragOverlay);
